<script setup>
import { computed } from 'vue';

const props = defineProps({
  label: { type: String, default: '' },
  variant: {
    type: String,
    default: 'neutral',
    validator: value =>
      [
        'neutral',
        'info',
        'success',
        'attention',
        'error',
        'new',
        'contacted',
        'scheduled',
        'offer',
        'won',
        'lost',
      ].includes(value),
  },
  selected: { type: Boolean, default: false },
  selectable: { type: Boolean, default: false },
  dismissible: { type: Boolean, default: false },
  disabled: { type: Boolean, default: false },
  size: {
    type: String,
    default: 'sm',
    validator: value => ['xs', 'sm'].includes(value),
  },
  dismissLabel: { type: String, default: 'Remover status' },
});

const emit = defineEmits(['click', 'dismiss', 'update:selected']);

const variantClasses = computed(
  () =>
    ({
      neutral: 'border-lais-border bg-lais-bg-muted text-lais-text',
      info: 'border-lais-info/30 bg-lais-info/10 text-lais-info',
      success: 'border-lais-success/30 bg-lais-success/10 text-lais-success',
      attention: 'border-lais-warning/40 bg-lais-warning/10 text-lais-warning',
      error: 'border-lais-danger/30 bg-lais-danger/10 text-lais-danger',
      new: 'border-lais-primary/30 bg-lais-primary-50 text-lais-primary',
      contacted: 'border-lais-info/30 bg-lais-info/10 text-lais-info',
      scheduled: 'border-lais-warning/40 bg-lais-warning/10 text-lais-warning',
      offer: 'border-lais-primary/30 bg-lais-primary-50 text-lais-primary',
      won: 'border-lais-success/30 bg-lais-success/10 text-lais-success',
      lost: 'border-lais-danger/30 bg-lais-danger/10 text-lais-danger',
    })[props.variant]
);

const sizeClasses = computed(() =>
  props.size === 'xs'
    ? 'min-h-5 px-2 text-lais-label-xs'
    : 'min-h-6 px-2.5 text-lais-label-sm'
);

const contentClass = computed(() => [
  'inline-flex min-w-0 items-center gap-1 rounded-full border font-semibold transition',
  sizeClasses.value,
  variantClasses.value,
  {
    'ring-2 ring-lais-primary-50': props.selected,
    'cursor-pointer hover:shadow-lais-card focus-visible:outline focus-visible:outline-2 focus-visible:outline-lais-primary-50':
      props.selectable && !props.disabled,
    'cursor-not-allowed opacity-60': props.disabled,
  },
]);

const handleSelect = event => {
  if (!props.selectable || props.disabled) return;

  emit('update:selected', !props.selected);
  emit('click', event);
};

const handleDismiss = event => {
  if (props.disabled) return;

  emit('dismiss', event);
};
</script>

<template>
  <span class="inline-flex max-w-full items-center gap-1">
    <button
      v-if="selectable"
      type="button"
      :class="contentClass"
      :aria-pressed="selected"
      :disabled="disabled"
      @click="handleSelect"
    >
      <span class="truncate">
        <slot>{{ label }}</slot>
      </span>
    </button>
    <span v-else :class="contentClass">
      <span class="truncate">
        <slot>{{ label }}</slot>
      </span>
    </span>
    <button
      v-if="dismissible"
      type="button"
      class="inline-flex size-5 items-center justify-center rounded-full text-lais-text-muted transition hover:bg-lais-bg-muted hover:text-lais-text focus-visible:outline focus-visible:outline-2 focus-visible:outline-lais-primary-50 disabled:cursor-not-allowed disabled:opacity-60"
      :aria-label="dismissLabel"
      :disabled="disabled"
      @click.stop="handleDismiss"
    >
      <span class="i-lucide-x size-3" aria-hidden="true" />
    </button>
  </span>
</template>
